import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'

export interface CommandAction {
  id: string
  label: string
  hint?: string
  group?: string
  icon?: ReactNode
  /** Keywords for fuzzy match. */
  keywords?: string[]
  perform: () => void | Promise<void>
}

interface CommandRegistryValue {
  open: boolean
  setOpen: (open: boolean) => void
  actions: CommandAction[]
  register: (actions: CommandAction[]) => () => void
}

const CommandRegistryContext = createContext<CommandRegistryValue | null>(null)

export function CommandRegistryProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [registrations, setRegistrations] = useState<
    Record<string, CommandAction[]>
  >({})

  const register = useCallback((actions: CommandAction[]): (() => void) => {
    const id = crypto.randomUUID()
    setRegistrations((prev) => ({ ...prev, [id]: actions }))
    return () => {
      setRegistrations((prev) => {
        const next = { ...prev }
        delete next[id]
        return next
      })
    }
  }, [])

  const actions = useMemo(
    () => Object.values(registrations).flat(),
    [registrations]
  )

  return (
    <CommandRegistryContext.Provider
      value={{ open, setOpen, actions, register }}
    >
      {children}
    </CommandRegistryContext.Provider>
  )
}

export function useCommandRegistry(): CommandRegistryValue {
  const ctx = useContext(CommandRegistryContext)
  if (!ctx)
    throw new Error(
      'useCommandRegistry must be used inside <CommandRegistryProvider>'
    )
  return ctx
}
